import { categories } from './types';
/**
 * Creates batches of events, then sends them at intervals.
 */
class Batcher {
    constructor(backendUrl, getToken, init, standalone) {
        this.backendUrl = backendUrl;
        this.getToken = getToken;
        this.init = init;
        this.standalone = standalone;
        this.autosendInterval = 5 * 1000;
        this.retryTimeout = 3 * 1000;
        this.retryLimit = 3;
        this.apiEdp = '/v1/sdk/i';
        this.batch = {
            [categories.people]: [],
            [categories.events]: [],
        };
        this.intervalId = null;
        this.stopped = false;
        this.paused = false;
        this.onVisibilityChange = () => {
            if (document.hidden) {
                // The tab may be closing; flush now with keepalive so buffered events
                // survive the transition instead of waiting for the (paused) interval.
                this.flush(true);
                this.paused = true;
            }
            else {
                this.paused = false;
            }
        };
    }
    getBatches() {
        this.batch[categories.people] = this.dedupePeopleEvents();
        const finalData = {
            data: {
                [categories.people]: [...this.batch[categories.people]],
                [categories.events]: [...this.batch[categories.events]],
            },
        };
        return finalData;
    }
    addEvent(event) {
        this.batch[event.category].push(event.data);
    }
    sendImmediately(event) {
        this.sendBatch({ [event.category]: [event.data] });
    }
    /**
     *
     * Essentially we're dividing the batch by identify events and squash all same category events into one in each part,
     * taking priority to the last one
     */
    dedupePeopleEvents() {
        const peopleEvents = this.batch[categories.people];
        const finalEvents = [];
        const currentPart = [];
        for (let event of peopleEvents) {
            if (event.type === 'identity') {
                if (currentPart.length > 0) {
                    finalEvents.push(...this.squashPeopleEvents(currentPart), event);
                    currentPart.length = 0;
                }
                else {
                    finalEvents.push(event);
                }
            }
            else {
                currentPart.push(event);
            }
        }
        if (currentPart.length > 0) {
            finalEvents.push(...this.squashPeopleEvents(currentPart));
        }
        return finalEvents;
    }
    squashPeopleEvents(events) {
        if (!events || events.length === 0) {
            return [];
        }
        // keyed by type + user_id: the backend drops actions without a user_id, and merging
        // mutations aimed at different users would misattribute them
        const uniqueEventsByType = new Map();
        for (let event of events) {
            const eventKey = `${event.type}|${event.user_id ?? ''}`;
            const prev = uniqueEventsByType.get(eventKey);
            if (prev) {
                if (event.type === 'increment_property') {
                    const previousValues = Object.entries(prev.payload);
                    const currentValues = Object.entries(event.payload);
                    const uniqueKeys = new Set([...previousValues.map(([key]) => key), ...currentValues.map(([key]) => key)]);
                    const mergedPayload = {};
                    uniqueKeys.forEach((key) => {
                        const prevValue = typeof prev.payload[key] === 'number' ? prev.payload[key] : 0;
                        const currValue = typeof event.payload[key] === 'number' ? event.payload[key] : 0;
                        mergedPayload[key] = prevValue + currValue;
                    });
                    uniqueEventsByType.set(eventKey, {
                        type: event.type,
                        user_id: event.user_id,
                        timestamp: event.timestamp,
                        payload: mergedPayload,
                    });
                    continue;
                }
                // merge payloads, taking priority to the latest one
                uniqueEventsByType.set(eventKey, {
                    type: event.type,
                    user_id: event.user_id,
                    timestamp: event.timestamp,
                    payload: { ...(prev.payload ?? {}), ...(event.payload ?? {}) },
                });
            }
            else {
                uniqueEventsByType.set(eventKey, event);
            }
        }
        return Array.from(uniqueEventsByType.values());
    }
    sendBatch(batch, keepalive = false, onTerminalFailure) {
        if (this.stopped) {
            onTerminalFailure?.();
            return;
        }
        const sentBatch = batch;
        let attempts = 0;
        const send = () => {
            const token = this.getToken();
            if (!token) {
                // No token yet: keep the events so they go out once one is available.
                onTerminalFailure?.();
                return;
            }
            attempts++;
            return fetch(`${this.backendUrl}${this.apiEdp}`, {
                method: 'POST',
                keepalive,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(sentBatch),
            })
                .then(async (response) => {
                if (response.status === 401) {
                    const body = await response.json().catch(() => null);
                    if (!this.standalone && body?.error === 'token expired') {
                        this.stop();
                        return;
                    }
                    if (attempts < this.retryLimit) {
                        return this.init().then(() => {
                            send();
                        });
                    }
                    onTerminalFailure?.();
                    return;
                }
                if (response.status === 403) {
                    if (attempts < this.retryLimit) {
                        return this.init().then(() => {
                            send();
                        });
                    }
                    onTerminalFailure?.();
                    return;
                }
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            })
                .catch(() => {
                if (attempts < this.retryLimit) {
                    setTimeout(() => void send(), this.retryTimeout);
                }
                else {
                    onTerminalFailure?.();
                }
            });
        };
        void send();
    }
    startAutosend() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        this.paused = document.hidden;
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.intervalId = setInterval(() => {
            if (!this.paused) {
                this.flush();
            }
        }, this.autosendInterval);
    }
    flush(keepalive = false) {
        const cats = Object.keys(this.batch);
        const isEmpty = cats.every((category) => this.batch[category].length === 0);
        if (isEmpty) {
            return;
        }
        const batches = this.getBatches();
        // Snapshot what we're about to send so we can restore it if the request
        // ultimately fails, instead of dropping events the moment we clear the buffer.
        const snapshot = {
            [categories.people]: [...batches.data[categories.people]],
            [categories.events]: [...batches.data[categories.events]],
        };
        cats.forEach((key) => {
            this.batch[key] = [];
        });
        this.sendBatch(batches, keepalive, () => this.requeue(snapshot));
    }
    requeue(snapshot) {
        if (this.stopped) {
            return;
        }
        // Put the failed events back at the front, ahead of anything queued since,
        // so they're retried on the next flush rather than lost.
        this.batch[categories.people] = [
            ...snapshot[categories.people],
            ...this.batch[categories.people],
        ];
        this.batch[categories.events] = [
            ...snapshot[categories.events],
            ...this.batch[categories.events],
        ];
    }
    stop() {
        this.flush();
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopped = true;
    }
    restart() {
        this.stopped = false;
        this.startAutosend();
    }
}
export default Batcher;
